export type DateFormatStyle =
  | "medium"
  | "mediumTime"
  | "short"
  | "shortTime"
  | "monthYear"
  | "year"
const defaultLocale = "en"
const dateFormatOptions: Record<DateFormatStyle, Intl.DateTimeFormatOptions> = {
  // medium date e.g. 'Feb 17, 2022'
  medium: { year: "numeric", month: "short", day: "2-digit" },
  // medium date with time e.g. 'Feb 17, 2022, 15:36'
  mediumTime: {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  },
  // short date e.g. '02/17/2022'
  short: { year: "numeric", month: "2-digit", day: "2-digit" },
  // short date with time e.g. '02/17/2022, 15:36'
  shortTime: {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  },
  // month and year only e.g. 'April 2022'
  monthYear: { month: "long", year: "numeric" },
  // year only e.g. '2022'
  year: { year: "numeric" },
}

/**
 * Takes a locale and returns a supported DateTime locale. The business logic is:
 * - If the language is `en` and the country is `US`, show the `MM/DD/YYYY` format (`en-US`)
 * - If the language is `en` and the country is not `US`, show the `DD/MM/YYYY` format (`en-GB`)
 * - If the language is `ja`, show the `YYYY年MM月DD日` format (`ja-JP`)
 * - If the language is `de`, show the `MM.DD.YYYY` format (`de-DE`)
 *
 * @param locale Locale to check (e.g. 'en', 'en-US', 'en-FR')
 * @returns Supported locale ('en-US', 'en-GB', 'ja-JP' and 'de-DE' are supported)
 */
function normalizeDateTimeLocale(locale = defaultLocale): string {
  const [language, region] = locale.split("-")

  switch (language) {
    case "en":
      return region === "US" ? "en-US" : "en-GB"
    case "de":
      return "de-DE"
    case "ja":
      return "ja-JP"
    default:
      return "en-US"
  }
}

/**
 * Takes an array of strings and returns string with a localized separator between each item.
 * We don’t use the Intl.ListFormat API because it’s not supported in recent Safari versions and we don’t want to
 * polyfill it for our limited use case.
 * @example
 * ```ts
 * const items = ['a', 'b', 'c'];
 * const list = listFormat(items);
 * // 'a, b, c'
 * const japaneseList = listFormat(items, { locale: 'ja' });
 * // 'a、b、c'
 */
export function listFormat(
  array: Array<string>,
  options: { locale?: string } = {},
): string {
  const { locale = "en" } = options
  if (locale.startsWith("ja")) return array.join("、")
  return array.join(", ")
}

/**
 * Converts a date to a localized date string
 * @param date The date to convert
 * @param options.style The style of date to format (default 'shortTime')
 * @param options.locale Locale to use for currency formatting (default 'en')
 * @returns Locale formatted currency string
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat
 *
 * @example
 * ```
 * const date = new Date();
 * const formattedDate = formatDate(date, 'short');
 * // formattedDate is '02/17/2022'
 */
export function dateFormat(
  date: Date,
  options: {
    style?: DateFormatStyle
    locale?: string
  },
): string {
  const { locale = defaultLocale, style = "shortTime" } = options
  return Intl.DateTimeFormat(
    normalizeDateTimeLocale(locale),
    dateFormatOptions[style],
  ).format(date)
}
